/* Hero Video Slider Carousel */
(function () {
  'use strict';

  class HeroVideoSlider {
    constructor(el) {
      this.el = el;
      this.slides = Array.from(el.querySelectorAll('[data-hvs-slide]'));
      this.dots = Array.from(el.querySelectorAll('[data-hvs-dot]'));
      this.prevBtn = el.querySelector('[data-hvs-prev]');
      this.nextBtn = el.querySelector('[data-hvs-next]');
      this.progressBar = el.querySelector('[data-hvs-progress-bar]');

      this.current = 0;
      this.total = this.slides.length;
      this.autoplay = el.dataset.autoplay === 'true';
      this.interval = parseInt(el.dataset.interval, 10) || 6000;
      this.transition = parseFloat(el.dataset.transition) || 800; // already ms (liquid x1000)
      this.pauseOnHover = el.dataset.pauseOnHover === 'true';
      this.loop = el.dataset.loop === 'true';
      this.transitionType = el.dataset.transitionType || 'fade';
      this.lumaOpts = {
        durationMs: this.transition,
        direction: el.dataset.lumaDirection || 'light',
        glowColor: el.dataset.lumaGlowColor || '#ffffff',
        glow: parseFloat(el.dataset.lumaGlow || '1.2'),
        band: (parseFloat(el.dataset.lumaBand || '6') / 100),
        soft: (parseFloat(el.dataset.lumaSoft || '12') / 100),
        live: el.dataset.lumaLive === 'true'
      };
      this.timer = null;
      this.progressStart = 0;
      this.progressRaf = null;
      this.io = null;
      this.inView = true;

      if (this.total <= 0) return;

      this.bind();
      this.preloadVideos();
      this.go(0, true);
      this.setupVisibility();
      if (this.autoplay && this.total > 1) this.start();
    }

    setupVisibility() {
      // Pause the ACTIVE video + autoplay timer when the slider scrolls out of
      // view, resume when it returns. Leaves preloadVideos alone so every clip
      // still buffers normally on load (no fallback-only first run).
      if (!('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver((entries) => {
        const en = entries[0];
        if (!en) return;
        this.inView = en.isIntersecting;
        const active = this.slides[this.current];
        const video = active ? active.querySelector('video') : null;
        if (en.isIntersecting) {
          if (video) { const p = video.play(); if (p && p.catch) p.catch(() => {}); }
          if (this.autoplay && this.total > 1) this.start();
        } else {
          if (video) { try { video.pause(); } catch (e) {} }
          this.stop();
        }
      }, { rootMargin: '150px' });
      this.io.observe(this.el);
    }

    destroy() {
      this.stop();
      if (this.io) { try { this.io.disconnect(); } catch (e) {} this.io = null; }
      this.slides.forEach((slide) => {
        const v = slide.querySelector('video');
        if (v) { try { v.pause(); } catch (e) {} }
      });
    }

    bind() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => { this.prev(); this.restart(); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.next(); this.restart(); });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.hvsDot, 10);
          this.go(idx);
          this.restart();
        });
      });

      if (this.pauseOnHover) {
        this.el.addEventListener('mouseenter', () => this.stop());
        this.el.addEventListener('mouseleave', () => { if (this.autoplay) this.start(); });
      }

      // Pause when tab not visible
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else if (this.autoplay) this.start();
      });

      // ── Touch swipe support (v2, 2026-08-28) ─────────────────────────
      // Previous version listened to touchstart/touchend ONLY. When the
      // browser reclaimed the gesture mid-swipe (native text selection over
      // the centered content, long-press, page scroll, second finger) it
      // fires touchcancel and NEVER fires touchend -- the swipe was silently
      // dropped. That produced the "works on one side, fails across the
      // middle, sometimes fails anywhere" behaviour.
      //
      // This version: single-touch guard, axis lock on first move, explicit
      // touchcancel reset. Stays passive:true (touch-action:pan-y in the CSS
      // does the scroll arbitration) so scroll performance is unchanged.
      const SWIPE_MIN = 40;   // px of horizontal travel required to commit
      const AXIS_LOCK = 10;   // px of travel before we decide the axis

      let tActive = false;    // a single-finger gesture is in progress
      let tDecided = false;   // axis has been locked for this gesture
      let tIsSwipe = false;   // locked axis is horizontal
      let tStartX = 0;
      let tStartY = 0;
      let tLastX = 0;
      let tId = null;         // identifier of the finger we are tracking

      const resetTouch = () => {
        tActive = false; tDecided = false; tIsSwipe = false; tId = null;
      };

      this.el.addEventListener('touchstart', (e) => {
        // Ignore additional fingers; a pinch/second touch is not a swipe.
        if (e.touches.length !== 1) { resetTouch(); return; }
        const t = e.changedTouches[0];
        tActive = true; tDecided = false; tIsSwipe = false;
        tId = t.identifier;
        tStartX = tLastX = t.clientX;
        tStartY = t.clientY;
      }, { passive: true });

      this.el.addEventListener('touchmove', (e) => {
        if (!tActive) return;
        if (e.touches.length !== 1) { resetTouch(); return; }

        // Only track the finger we started with.
        let t = null;
        for (let i = 0; i < e.changedTouches.length; i++) {
          if (e.changedTouches[i].identifier === tId) { t = e.changedTouches[i]; break; }
        }
        if (!t) return;

        tLastX = t.clientX;

        if (!tDecided) {
          const dx = Math.abs(t.clientX - tStartX);
          const dy = Math.abs(t.clientY - tStartY);
          if (dx > AXIS_LOCK || dy > AXIS_LOCK) {
            tDecided = true;
            tIsSwipe = dx > dy;      // horizontal intent -> our gesture
            if (!tIsSwipe) tActive = false;  // vertical -> let the page scroll
          }
        }
      }, { passive: true });

      this.el.addEventListener('touchend', (e) => {
        if (!tActive) { resetTouch(); return; }

        // Prefer the tracked finger's final position, else the last move.
        let endX = tLastX;
        for (let i = 0; i < e.changedTouches.length; i++) {
          if (e.changedTouches[i].identifier === tId) { endX = e.changedTouches[i].clientX; break; }
        }

        const diff = tStartX - endX;
        // If the gesture never moved far enough to lock an axis, still allow
        // a fast flick that cleared the commit threshold.
        const eligible = tIsSwipe || !tDecided;
        if (eligible && Math.abs(diff) > SWIPE_MIN) {
          if (diff > 0) this.next(); else this.prev();
          this.restart();
        }
        resetTouch();
      }, { passive: true });

      // The handler that was missing. Browser took the gesture -> drop it
      // cleanly instead of leaving stale coords that corrupt the next swipe.
      this.el.addEventListener('touchcancel', resetTouch, { passive: true });

      // Keyboard navigation
      this.el.setAttribute('tabindex', '0');
      this.el.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { this.prev(); this.restart(); }
        if (e.key === 'ArrowRight') { this.next(); this.restart(); }
      });
    }

    preloadVideos() {
      // Ensure all videos try to play muted (browser autoplay policy)
      this.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (video) {
          video.muted = true;
          video.setAttribute('muted', '');
          video.playsInline = true;
          const tryPlay = () => {
            const p = video.play();
            if (p && typeof p.catch === 'function') {
              p.catch(() => {
                // Will be retried on user interaction
              });
            }
          };
          tryPlay();
          video.addEventListener('loadeddata', tryPlay, { once: true });
        }
      });
    }

    go(index, immediate = false) {
      if (this.total === 0) return;
      let next = index;
      if (next < 0) next = this.loop ? this.total - 1 : 0;
      if (next >= this.total) next = this.loop ? 0 : this.total - 1;

      const prevSlide = this.slides[this.current];
      const nextSlide = this.slides[next];

      const staleOv = this.el.querySelector('.hvs-luma-overlay');
      if (staleOv && staleOv.parentNode) staleOv.parentNode.removeChild(staleOv);

      const lumaSnap = (!immediate && this.transitionType === 'lumasweep' &&
        prevSlide && nextSlide && prevSlide !== nextSlide && window.HVSLumaSweep)
        ? (window.HVSLumaSweep.snapshotLive(prevSlide) || window.HVSLumaSweep.snapshot(prevSlide)) : null;

      const prevVideo = prevSlide ? prevSlide.querySelector('video') : null;
      const willLive = !!(lumaSnap && this.lumaOpts.live && prevVideo);

      // When the sweep can't run (mobile / reduced-motion / no frame), fall back to a zoom
      // instead of a plain fade. Toggle before the class swap so the zoom from-state is set.
      if (this.transitionType === 'lumasweep') {
        this.el.classList.toggle('hero-video-slider--lumafallback', !lumaSnap);
      }

      this.slides.forEach((s, i) => {
        s.classList.remove('is-active', 'is-prev');
        if (i === next) s.classList.add('is-active');
      });
      if (prevSlide && prevSlide !== nextSlide) prevSlide.classList.add('is-prev');

      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === next));

      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (i === next) {
          video.currentTime = 0;
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!(willLive && video === prevVideo)) {
          try { video.pause(); } catch (e) {}
        }
      });

      this.current = next;
      this.resetProgress();

      if (lumaSnap) {
        // Mount the sweep inside the now-active slide so the slide's real pink overlay
        // (and text) render on top of it — looks identical to a static slide.
        const lo = Object.assign({}, this.lumaOpts, { mount: nextSlide, liveVideo: willLive ? prevVideo : null });
        try { window.HVSLumaSweep.dissolve(this.el, lumaSnap, lo); } catch (e) {}
      }
    }

    next() { this.go(this.current + 1); }
    prev() { this.go(this.current - 1); }

    start() {
      if (this.total <= 1) return;
      if (this.inView === false) return;
      this.stop();
      this.progressStart = performance.now();
      this.tickProgress();
      this.timer = setInterval(() => this.next(), this.interval);
    }

    stop() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
      if (this.progressRaf) { cancelAnimationFrame(this.progressRaf); this.progressRaf = null; }
    }

    restart() {
      if (this.autoplay) this.start();
    }

    resetProgress() {
      if (!this.progressBar) return;
      this.progressBar.style.transition = 'none';
      this.progressBar.style.width = '0%';
      // force reflow
      void this.progressBar.offsetWidth;
      this.progressStart = performance.now();
    }

    tickProgress() {
      if (!this.progressBar) return;
      const step = () => {
        const elapsed = performance.now() - this.progressStart;
        const pct = Math.min(100, (elapsed / this.interval) * 100);
        this.progressBar.style.width = pct + '%';
        if (pct < 100 && this.timer) {
          this.progressRaf = requestAnimationFrame(step);
        }
      };
      step();
    }
  }

  function init() {
    document.querySelectorAll('[data-hvs]').forEach((el) => {
      if (!el.__hvs) el.__hvs = new HeroVideoSlider(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when section is reloaded in theme editor
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('[data-hvs]');
    if (el) {
      if (el.__hvs && el.__hvs.destroy) el.__hvs.destroy();
      el.__hvs = new HeroVideoSlider(el);
    }
  });
  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target.querySelector('[data-hvs]');
    if (el && el.__hvs) {
      if (el.__hvs.destroy) el.__hvs.destroy(); else el.__hvs.stop();
      el.__hvs = null;
    }
  });
})();
